import { useState, useEffect } from 'react'
import { ExternalLink, AlertTriangle, Download } from 'lucide-react'
import { documentsApi } from '../../services/api'

interface PdfViewerProps {
  documentId: string
  title?: string
}

export default function PdfViewer({ documentId, title }: PdfViewerProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null

    const fetchPdf = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await documentsApi.getFile(documentId)
        const blob = new Blob([response.data], { type: 'application/pdf' })
        objectUrl = URL.createObjectURL(blob)
        setBlobUrl(objectUrl)
      } catch (err) {
        console.error('Failed to load PDF:', err)
        setError('Nie udalo sie zaladowac pliku PDF')
      } finally {
        setLoading(false)
      }
    }

    fetchPdf()

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [documentId])

  const handleDownload = async () => {
    try {
      const response = await documentsApi.getFile(documentId, true)
      const blob = new Blob([response.data])
      const url = URL.createObjectURL(blob)
      const link = window.document.createElement('a')
      link.href = url
      link.download = `${title || 'dokument'}.pdf`
      window.document.body.appendChild(link)
      link.click()
      window.document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download failed:', err)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (error || !blobUrl) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
        <p className="text-gray-600 mb-4">{error || 'Nie mozna wyswietlic pliku PDF'}</p>
        <button onClick={handleDownload} className="btn-primary inline-flex items-center">
          <Download className="w-4 h-4 mr-2" />
          Pobierz plik
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <button
          onClick={() => window.open(blobUrl, '_blank')}
          className="btn-secondary text-sm"
        >
          <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
          Otworz w nowej karcie
        </button>
      </div>
      <iframe
        src={blobUrl}
        className="w-full rounded-lg border"
        style={{ height: 'calc(100vh - 280px)', minHeight: '600px' }}
        title={title || 'PDF Preview'}
      />
    </div>
  )
}
